/**
 * Página principal de Relatórios
 * Contém 4 seções navegáveis por âncoras:
 * - Imóveis
 * - Cenário AVCB
 * - Cenário Alvarás
 * - Desempenho
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileDown, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useRelatoriosFilters } from './hooks';
import { API_BASE_URL } from '@/lib/api';
import type { ImoveisUnitLine } from './hooks/useRelatoriosData';
import {
  SectionNav,
  ImoveisSection,
  CenarioAvcbSection,
  CenarioAlvarasSection,
  DesempenhoSection,
} from './components';

export function RelatoriosTab() {
  const navigate = useNavigate();
  const { setActiveSection, regional, uf, imoveisScope } = useRelatoriosFilters();
  const [exportingStores, setExportingStores] = useState(false);

  const exportStores = async () => {
    setExportingStores(true);
    try {
      const url = new URL(`${API_BASE_URL}/claro/api/reports/imoveis`, window.location.origin);
      url.searchParams.set('details', 'true');
      url.searchParams.set('scope', imoveisScope);
      if (regional) url.searchParams.set('regional', regional);
      if (uf) url.searchParams.set('uf', uf);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json() as { result?: { units?: ImoveisUnitLine[] } };
      const stores = (payload.result?.units ?? []).filter((unit) => {
        const text = `${unit.tipo} ${unit.subtipo} ${unit.unidade}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return text.includes('loja');
      });
      if (!stores.length) {
        alert('Nenhuma loja foi encontrada com os filtros atuais.');
        return;
      }
      const rows = stores.map((unit) => ({
        Loja: unit.unidade,
        Identificação: unit.identificacao,
        Tipo: unit.tipo,
        Subtipo: unit.subtipo || '—',
        Regional: unit.regional || '—',
        UF: unit.uf || '—',
        Cidade: unit.cidade || '—',
        'Status Alvará': unit.alvaraStatus || 'Sem documento',
        'Status AVCB': unit.avcbStatus || 'Sem documento',
        Classificação: unit.classificacao,
      }));
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet['!cols'] = [
        { wch: 34 }, { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 16 },
        { wch: 8 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 16 },
      ];
      if (sheet['!ref']) sheet['!autofilter'] = { ref: sheet['!ref'] };
      XLSX.utils.book_append_sheet(workbook, sheet, 'Lojas');
      XLSX.writeFile(workbook, `lojas-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar lojas:', error);
      alert('Não foi possível exportar as lojas. Tente novamente.');
    } finally {
      setExportingStores(false);
    }
  };

  // Detecta scroll e atualiza seção ativa
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['imoveis', 'cenario-avcb', 'cenario-alvaras', 'desempenho'];
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Se o topo da seção está na metade superior da viewport
          if (rect.top <= 150 && rect.bottom > 150) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setActiveSection]);

  // Detecta hash na URL para navegação direta
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setActiveSection(hash);
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [setActiveSection]);

  return (
    <div className="flex gap-6">
      {/* Menu lateral - Desktop */}
      <div className="hidden lg:block w-48 flex-shrink-0">
        <div className="sticky top-20">
          <SectionNav />
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 min-w-0 space-y-16 pb-16">
        {/* Header da página */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Relatórios</h1>
              <p className="text-muted-foreground mt-1">
                Visão consolidada de imóveis, cenários de documentos e desempenho de fornecedores
              </p>
            </div>
            <div className="hidden gap-2 sm:flex">
              <Button
                onClick={exportStores}
                variant="outline"
                className="gap-2"
                disabled={exportingStores}
              >
                {exportingStores ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {exportingStores ? 'Exportando...' : 'Exportar lojas'}
              </Button>
              <Button 
                onClick={() => navigate('/relatorio/print')}
                className="gap-2"
              >
                <FileDown className="h-4 w-4" />
                Gerar Relatório PDF
              </Button>
            </div>
          </div>
          
          {/* Menu mobile */}
          <div className="lg:hidden mt-4 flex items-center gap-2">
            <div className="flex-1">
              <MobileNav />
            </div>
            <Button
              onClick={exportStores}
              variant="outline"
              size="icon"
              className="sm:hidden"
              disabled={exportingStores}
              title="Exportar lojas"
            >
              {exportingStores ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
            <Button 
              onClick={() => navigate('/relatorio/print')}
              variant="outline"
              size="icon"
              className="sm:hidden"
            >
              <FileDown className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Seções */}
        <ImoveisSection />
        <CenarioAvcbSection />
        <CenarioAlvarasSection />
        <DesempenhoSection />
      </div>
    </div>
  );
}

// ============================================================================
// NAVEGAÇÃO MOBILE
// ============================================================================

function MobileNav() {
  const { activeSection, setActiveSection } = useRelatoriosFilters();

  const sections = [
    { id: 'imoveis', label: 'Imóveis' },
    { id: 'cenario-avcb', label: 'AVCB' },
    { id: 'cenario-alvaras', label: 'Alvarás' },
    { id: 'desempenho', label: 'Desempenho' },
  ];

  const handleClick = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex gap-1 p-1 rounded-lg bg-muted overflow-x-auto">
      {sections.map((section) => (
        <button
          key={section.id}
          onClick={() => handleClick(section.id)}
          className={cn(
            'px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
            activeSection === section.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {section.label}
        </button>
      ))}
    </div>
  );
}
